'use client';

import { suggest, SuggestResponseItem } from '@yandex/ymaps3-types';
import { useTranslations } from 'next-intl';
import { Key, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import SearchIcon from '@/fo/icons/base/icons/search.svg';
import { UiKitAutocompleteBase } from '@/fo/ui/kit/autocomplete/base';
import { BaseOption } from '@/fo/ui/kit/listbox/base';

import { getAddressFromSuggest } from '../../helpers/getAddressFromSuggest';
import { highlightText } from '../../helpers/highlightText';

import { styles } from './SearchLocationField.styles';

const getSuggestKey = (result: SuggestResponseItem) =>
  result.value +
  result.uri +
  result.title.text +
  result.subtitle?.text +
  result.type;

export interface SearchLocationFieldProps {
  suggest: typeof suggest;
  onSelect: (option: SuggestResponseItem | null) => void;
  city?: string;
}

export function SearchLocationField({
  suggest,
  onSelect,
  city
}: SearchLocationFieldProps) {
  const slots = styles();

  const t = useTranslations();

  const [suggestResults, setSuggestResults] = useState<SuggestResponseItem[]>(
    []
  );

  const [currentValue, setCurrentValue] = useState<string>('');

  const handleSelect = (key: Key | null) => {
    const result = suggestResults.find(
      (result) => getSuggestKey(result) === key
    );

    onSelect(result ?? null);
  };

  const debouncedSuggest = useDebouncedCallback(async (value: string) => {
    const results = await suggest({
      text: `${city ?? ''} ${value}`.trim()
    });

    const uniqueResultsMap = new Map<string, SuggestResponseItem>();
    results.forEach((result) => {
      const key = getSuggestKey(result);
      if (!uniqueResultsMap.has(key)) {
        uniqueResultsMap.set(key, result);
      }
    });

    setSuggestResults(Array.from(uniqueResultsMap.values()));
    setCurrentValue(value);
  }, 300);

  return (
    <div className={slots.base()}>
      <UiKitAutocompleteBase<SuggestResponseItem & BaseOption>
        options={suggestResults.map((result) => ({
          ...result,
          value: getSuggestKey(result),
          label: getAddressFromSuggest(result).trim()
        }))}
        startAdornment={<SearchIcon />}
        onChange={debouncedSuggest}
        onSelect={handleSelect}
        placeholder={t('findAddress')}
        renderOption={(option) => (
          <span>
            {highlightText(option.label, currentValue).map((part) => (
              <span
                key={part.value}
                className={slots.text({ highlight: part.type === 'highlight' })}
              >
                {part.value}
              </span>
            ))}
          </span>
        )}
      />
    </div>
  );
}
